import { Controller } from "@hotwired/stimulus"
import { Tab } from "bootstrap"

// Shared by three forms: the new-contract form, the draft-contract edit
// form, and the admin template editor — each wires only the targets/actions
// it needs (see data-action in each view).
//
// On the new-contract form, the editor holds raw "{{group.key}}" text plus
// whatever free-text edits the user makes: applying a template just loads
// its untouched body, and the merge-field toolbar inserts the literal token
// (like the template editor — see ContractsHelper#merge_field_token_groups).
// The "Pré-visualizar" tab is the only place placeholders ever get resolved
// against real data, and that resolution happens server-side (see
// ContractsController#preview) against whatever HTML the editor currently
// holds (including user edits) — #create resolves the exact same way before
// persisting, so what actually gets saved always matches the last preview.
// This intentionally avoids ever resolving field values client-side — the
// source of earlier bugs here — the server is the only place that ever
// substitutes real data into a template; the client's only job is to hand
// over the editor's current raw HTML.
export default class extends Controller {
  static targets = [
    "templateSelect", "content", "orderSelect", "organizationSignerSelect",
    "contentTab", "previewTab",
    "previewForm", "previewOrderId", "previewTemplateId", "previewOrganizationSignerId", "previewContent"
  ]
  static values = { fields: Object }

  applyTemplate() {
    const html = this.templateSelectTarget.selectedOptions[0]?.dataset.body || ""
    this.contentTarget.editor?.loadHTML(html)
  }

  // Inserts a merge field's "{{group.key}}" token (new-contract form and
  // template editor) or its resolved value (draft-contract edit form — see
  // ContractsHelper#merge_field_groups) at the current cursor position.
  insert({ params: { field } }) {
    const entry = this.fieldsValue[field]
    const editor = this.contentTarget.editor
    if (!entry || !editor) return

    entry.kind === "html" ? editor.insertHTML(entry.value) : editor.insertString(entry.value)
  }

  // Keeps the "Pré-visualizar" tab in sync with the order/template/signer
  // pickers and the editor's current content: enabled only once an order
  // and a template are both selected (per contract), and resubmitted via
  // the hidden previewForm on every relevant change (including clicking the
  // tab itself, to pick up any editing done since the last sync) — even
  // while it isn't the active tab, so it's already current by the time the
  // user switches to it. Posted rather than a GET src= because the editor's
  // HTML can be arbitrarily large. Falls back to the content tab if the
  // preview stops being available while it's the one showing.
  syncPreview() {
    if (!this.hasPreviewFormTarget || !this.hasPreviewTabTarget) return

    const orderId = this.hasOrderSelectTarget ? this.orderSelectTarget.value : ""
    const templateId = this.hasTemplateSelectTarget ? this.templateSelectTarget.value : ""

    if (!orderId || !templateId) {
      this.previewTabTarget.disabled = true
      if (this.previewTabTarget.classList.contains("active") && this.hasContentTabTarget) {
        Tab.getOrCreateInstance(this.contentTabTarget).show()
      }
      return
    }

    this.previewTabTarget.disabled = false

    this.previewOrderIdTarget.value = orderId
    this.previewTemplateIdTarget.value = templateId
    if (this.hasOrganizationSignerSelectTarget) this.previewOrganizationSignerIdTarget.value = this.organizationSignerSelectTarget.value
    if (this.hasContentTarget) this.previewContentTarget.value = this.contentTarget.innerHTML

    this.previewFormTarget.requestSubmit()
  }
}
